import React, {useState, useEffect} from 'react';
import {connect, useSelector} from 'react-redux';
import Menu, {SubMenu, Item as MenuItem} from 'rc-menu';
import {push} from 'connected-react-router';
import {SIDEBAR_WIDTH, HEADER_HEIGHT, FOOTER_HEIGHT} from '../../../conf/SMCConsts';
import {SMCPaths} from '../../../conf/Routes';
import '../sidebarStyles.css';
import {useTranslation} from 'react-i18next';
import {isEmptyPortalSelector, isUserAdminSelector, canViewLogsSelector, userPermissionsSelector, planPermissionsSelector, communityPermissionsSelector, gatewayPermissionsSelector, rolePermissionsSelector, customImagePermissionsSelector, serviceDomainSettingsPermissionsSelector, systemUserPermissionsSelector, isSystemUserSelector, isCyberViewsEnabledSelector, isSMELogsEnabledSelector, isUserMultiDomainAdminSelector} from '../../../store/selectors/user.selectors';
import {showSidebarSelector} from '../../../store/selectors/view.selectors';
import store from '../../../store';
import {toggleShowSidebar} from '../../../store/actions/view.actions';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import { makeStyles } from '@mui/styles';

const TOGGLE_ARROW_ROW_HEIGHT = 22;

function SMPMainSidebar({
    navigateTo,
    currentURLPath,
    isEmptyPortal,
    isUserAdmin,
    isUserMultiDomainAdmin,
    isSystemUser,
    userPermissions,
    planPermissions,
    communityPermissions,
    gatewayPermissions,
    rolePermissions,
    customImagePermissions,
    serviceDomainSettingsPermissions,
    systemUserPermissions,
    canViewLogs,
    isCyberViewsEnabled,
    isSMELogsEnabled
}) {
    const { t } = useTranslation();
    const [currentSelectedTab, setCurrentSelectedTab] = useState('/');
    const showSidebar = useSelector(showSidebarSelector);  

    const classes = useStyles({showSidebar});

    const showGatewayTab               = gatewayPermissions ? gatewayPermissions.read : true;
    const showPlanTab                  = planPermissions ? planPermissions.read : true;
    const showUserTab                  = userPermissions ? userPermissions.read : true;
    const showCommunityTab             = communityPermissions ? communityPermissions.read : true;
    const showServiceDomainSettingsTab = serviceDomainSettingsPermissions ? serviceDomainSettingsPermissions.read : true;
    const showRoleTab                  = rolePermissions ? rolePermissions.read : true;
    const showCustomImageTab           = customImagePermissions ? customImagePermissions.read : true;
    const showSystemUserTab            = isSystemUser && (systemUserPermissions ? systemUserPermissions.read : true);

    useEffect(() => {
        if (Object.values(SMCPaths).includes(currentURLPath)) {
            setCurrentSelectedTab(currentURLPath);
        }
    }, [currentURLPath]);

    return (
        <div className={classes.root}>
            {/* Toggle arrow */}
            <div className={classes.toggleArrowRoot}>
                <span className={classes.toggleArrowContainer} onClick={() => store.dispatch(toggleShowSidebar())}>
                    {
                        showSidebar ? (
                            <FontAwesomeIcon title="Collapse panel" icon="angle-left" />
                        ) : (
                            <FontAwesomeIcon title="Expand panel" icon="angle-right" />
                        )
                    }
                </span>
            </div>
            {/* Main menu */}
            <div className={classes.menuRoot}>
                <Menu
                    defaultOpenKeys={["home-submenu", "service-domain-submenu", "system-submenu", "logs-submenu", "cyber-views-submenu"]}
                    selectedKeys={[currentSelectedTab]}
                    triggerSubMenuAction="click"
                    onClick={info => navigateTo(info.key)}
                    // onSelect={info => setCurrentSelectedTab(info.key)}
                    mode="inline"
                    // expandIcon=" =>"
                >
                    {
                        // Only show these menu items on non-empty portals
                        !isEmptyPortal && (
                            <SubMenu key="home-submenu" title={t("SMP.MAIN_MENU.HOME")}>
                                <MenuItem key={SMCPaths.home}>{t("SMP.MAIN_MENU.OVERVIEW")}</MenuItem>
                                { showGatewayTab && ( <MenuItem key={SMCPaths.map}>{t("SMP.MAIN_MENU.MAP")}</MenuItem> ) }
                                { showGatewayTab && ( <MenuItem key={SMCPaths.gateways}>{t("SMP.MAIN_MENU.GATEWAYS")}</MenuItem> ) }
                                { showPlanTab && ( <MenuItem key={SMCPaths.plans}>{t("SMP.MAIN_MENU.PLANS")}</MenuItem> ) }
                                { showUserTab && ( <MenuItem key={SMCPaths.users}>{t("SMP.MAIN_MENU.USERS")}</MenuItem> ) }
                                { showCommunityTab && ( <MenuItem key={SMCPaths.communities}>{t("SMP.MAIN_MENU.COMMUNITIES")}</MenuItem> ) }
                            </SubMenu>
                        )
                    }
                    {
                        // Only show these menu items on non-empty portals
                        !isEmptyPortal && (
                            <SubMenu key="service-domain-submenu" title={t("SMP.MAIN_MENU.SERVICE_DOMAIN")}>
                                { showServiceDomainSettingsTab && ( <MenuItem key={SMCPaths.portalSettings}>{t("SMP.MAIN_MENU.SETTINGS")}</MenuItem> ) }
                                { showRoleTab && ( <MenuItem key={SMCPaths.roles}>{t("SMP.MAIN_MENU.ROLES")}</MenuItem> ) }
                                { showCustomImageTab && ( <MenuItem key={SMCPaths.images}>{t("SMP.MAIN_MENU.CUSTOM_IMAGES")}</MenuItem> ) }
                            </SubMenu>                        
                        )
                    }
                    {
                        (isUserAdmin || showSystemUserTab) && (
                            <SubMenu key="system-submenu" title={t("SMP.MAIN_MENU.SYSTEM")}>
                                { isUserAdmin && !isUserMultiDomainAdmin && ( <MenuItem key={SMCPaths.systemSettings}>{t("SMP.MAIN_MENU.SETTINGS")}</MenuItem> ) }
                                { isUserAdmin && ( <MenuItem key={SMCPaths.administrators}>{t("SMP.MAIN_MENU.ADMINISTRATORS")}</MenuItem> ) }
                                { ((isUserAdmin && !isUserMultiDomainAdmin) || showSystemUserTab) && ( <MenuItem key={SMCPaths.systemUsers}>{t("SMP.MAIN_MENU.USERS")}</MenuItem> ) }
                                { isUserAdmin && !isUserMultiDomainAdmin && ( <MenuItem key={SMCPaths.systemRoles}>{t("SMP.MAIN_MENU.ROLES")}</MenuItem> ) }
                                { isUserAdmin && !isUserMultiDomainAdmin && ( <MenuItem key={SMCPaths.firmwares}>{t("SMP.MAIN_MENU.FIRMWARE")}</MenuItem> ) }
                                { isUserAdmin && !isUserMultiDomainAdmin && ( <MenuItem key={SMCPaths.serviceDomains}>{t("SMP.MAIN_MENU.SERVICE_DOMAINS")}</MenuItem> ) }
                                { isUserAdmin && !isUserMultiDomainAdmin && ( <MenuItem key={SMCPaths.servers}>{t("SMP.MAIN_MENU.SMP_SERVERS")}</MenuItem> ) }
                                { isUserAdmin && !isUserMultiDomainAdmin && ( <MenuItem key={SMCPaths.tools}>{t("SMP.MAIN_MENU.TOOLS")}</MenuItem> ) }
                            </SubMenu>
                        )
                    }
                    {
                        (isUserAdmin || canViewLogs) && (
                            <SubMenu key="logs-submenu" title={t("SMP.MAIN_MENU.LOGS")}>
                                { (!isEmptyPortal && isSMELogsEnabled) && <MenuItem key={SMCPaths.gatewayLogs}>{t("SMP.MAIN_MENU.GATEWAY_LOGS")}</MenuItem> }
                                <MenuItem key={SMCPaths.systemLogs}>{t("SMP.MAIN_MENU.SYSTEM_LOGS")}</MenuItem>
                                <MenuItem key={SMCPaths.activityLogs}>{t("SMP.MAIN_MENU.ACTIVITY_LOGS")}</MenuItem>
                            </SubMenu>
                        )
                    }
                    {
                        // Only show these menu items on non-empty portals that have cyber views enabled
                        (!isEmptyPortal && isCyberViewsEnabled) && (
                            <SubMenu key="cyber-views-submenu" title={t("SMP.MAIN_MENU.CYBER_VIEWS")}>
                                <MenuItem key={SMCPaths.infectedHosts}>{t("SMP.MAIN_MENU.INFECTED_HOSTS")}</MenuItem>
                                <MenuItem key={SMCPaths.preventedAttacks}>{t("SMP.MAIN_MENU.PREVENTED_ATTACKS")}</MenuItem>
                                <MenuItem key={SMCPaths.detectedAttacks}>{t("SMP.MAIN_MENU.DETECTED_ATTACKS")}</MenuItem>
                                <MenuItem key={SMCPaths.attackTrends}>{t("SMP.MAIN_MENU.ATTACK_TRENDS")}</MenuItem>
                            </SubMenu>
                        )
                    }
                </Menu>
            </div>
        </div>
    );
}

const useStyles = makeStyles({
    root: props => ({
        borderRight: props.showSidebar ? '1px solid #E0E4E8' : undefined,
        paddingRight: props.showSidebar ? '9px' : undefined
    }),
    toggleArrowContainer: props => ({
        position: 'absolute',
        left: props.showSidebar ? 187 : 4,
        cursor: 'pointer'
    }),
    toggleArrowRoot: {
        height: TOGGLE_ARROW_ROW_HEIGHT
    },
    menuRoot: props => ({
        display: props.showSidebar ? 'block' : 'none',
        height: `calc(100vh - ${HEADER_HEIGHT+FOOTER_HEIGHT+TOGGLE_ARROW_ROW_HEIGHT}px)`,
        width: SIDEBAR_WIDTH - 10,
        minWidth: SIDEBAR_WIDTH - 10,
        overflowY: 'auto'
    })
});

const mapStateToProps = (state) => ({
    currentURLPath: state.router.location.pathname,
    isEmptyPortal: isEmptyPortalSelector(state),
    isUserAdmin: isUserAdminSelector(state),
    isUserMultiDomainAdmin: isUserMultiDomainAdminSelector(state),
    isSystemUser: isSystemUserSelector(state),
    userPermissions: userPermissionsSelector(state),
    planPermissions: planPermissionsSelector(state),
    communityPermissions: communityPermissionsSelector(state),
    gatewayPermissions: gatewayPermissionsSelector(state),
    rolePermissions: rolePermissionsSelector(state),
    customImagePermissions: customImagePermissionsSelector(state),
    serviceDomainSettingsPermissions: serviceDomainSettingsPermissionsSelector(state),
    canViewLogs: canViewLogsSelector(state),
    systemUserPermissions: systemUserPermissionsSelector(state),
    isCyberViewsEnabled: isCyberViewsEnabledSelector(state),
    isSMELogsEnabled: isSMELogsEnabledSelector(state)
});

const mapDispatchToProps = {
    navigateTo: push
}

export default connect(mapStateToProps, mapDispatchToProps)(SMPMainSidebar);